import React, { useState } from 'react';
import { EdgeProps, getBezierPath, EdgeLabelRenderer } from 'reactflow';
import { useTheme } from '../../../contexts/ThemeContext';
import './edgeAnimations.css';

interface CurvedEdgeData {
  type?: string;
  label?: string;
  sourceNodeId?: string;
  targetNodeId?: string;
  sourceNodeLabel?: string;
  targetNodeLabel?: string;
}

export const CurvedEdge: React.FC<EdgeProps<CurvedEdgeData>> = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  data,
  markerEnd,
  selected,
  source,
  target
}) => {
  const { darkMode } = useTheme();
  const [showTooltip, setShowTooltip] = useState(false);
  
  // Calculate the bezier path with custom curvature
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
    curvature: 0.25, // Nice curved appearance
  });

  // Get edge styling based on type and theme
  const getEdgeStyle = () => {
    const baseStyle = {
      strokeWidth: 2,
      fill: 'none',
      ...style
    };

    if (selected) {
      baseStyle.strokeWidth = 4; // Thicker when selected
      baseStyle.stroke = darkMode ? '#60a5fa' : '#3b82f6';
      baseStyle.filter = 'drop-shadow(0 0 8px rgba(59, 130, 246, 0.8)) drop-shadow(0 0 16px rgba(59, 130, 246, 0.4))';
      baseStyle.strokeDasharray = '8,4'; // Animated dash pattern
    }

    return baseStyle;
  };

  // Get colorful icon and background based on dependency type
  const getIconStyle = (dependencyType: string) => {
    switch (dependencyType) {
      case 'tool_assignment':
      case 'persona_assignment':
        return {
          icon: dependencyType === 'tool_assignment' ? '🔧' : '👤',
          bgColor: darkMode ? 'bg-green-800' : 'bg-green-100',
          textColor: darkMode ? 'text-green-200' : 'text-green-800',
          borderColor: darkMode ? 'border-green-600' : 'border-green-300',
          shadowColor: 'shadow-green-500/30'
        };
      case 'input_artifact':
      case 'output_artifact':
        return {
          icon: dependencyType === 'input_artifact' ? '📥' : '📤',
          bgColor: darkMode ? 'bg-purple-800' : 'bg-purple-100',
          textColor: darkMode ? 'text-purple-200' : 'text-purple-800',
          borderColor: darkMode ? 'border-purple-600' : 'border-purple-300',
          shadowColor: 'shadow-purple-500/30'
        };
      case 'enhances':
        return {
          icon: '✨',
          bgColor: darkMode ? 'bg-cyan-800' : 'bg-cyan-100',
          textColor: darkMode ? 'text-cyan-200' : 'text-cyan-800',
          borderColor: darkMode ? 'border-cyan-600' : 'border-cyan-300',
          shadowColor: 'shadow-cyan-500/30'
        };
      case 'blocks':
        return {
          icon: '🚫',
          bgColor: darkMode ? 'bg-red-800' : 'bg-red-100',
          textColor: darkMode ? 'text-red-200' : 'text-red-800',
          borderColor: darkMode ? 'border-red-600' : 'border-red-300',
          shadowColor: 'shadow-red-500/30'
        };
      case 'flow':
      case 'requires':
      default:
        return {
          icon: '→',
          bgColor: darkMode ? 'bg-amber-800' : 'bg-amber-100', // Golden like motherboard traces
          textColor: darkMode ? 'text-amber-200' : 'text-amber-800',
          borderColor: darkMode ? 'border-amber-600' : 'border-amber-300',
          shadowColor: 'shadow-amber-500/30'
        };
    }
  };

  const edgeStyle = getEdgeStyle();
  const iconStyle = getIconStyle(data?.type || 'default');

  // Get tooltip content
  const getTooltipContent = () => {
    const dependencyTypeLabels = {
      'tool_assignment': 'Tool Assignment',
      'persona_assignment': 'Persona Assignment',
      'input_artifact': 'Input Artifact',
      'output_artifact': 'Output Artifact',
      'enhances': 'Enhances',
      'blocks': 'Blocks',
      'flow': 'Workflow Flow',
      'requires': 'Requires'
    };

    const typeLabel = dependencyTypeLabels[data?.type] || 'Connection';
    
    return (
      <div className="text-xs">
        <div className="font-semibold">{typeLabel}</div>
        <div className="text-gray-500 dark:text-gray-400">
          From: {data?.sourceNodeLabel || source}
        </div>
        <div className="text-gray-500 dark:text-gray-400">
          To: {data?.targetNodeLabel || target}
        </div>
      </div>
    );
  };

  return (
    <>
      <path
        id={id}
        style={edgeStyle}
        className="react-flow__edge-path"
        d={edgePath}
        markerEnd={markerEnd}
      />
      
      {/* Colorful icon label with hover tooltip */}
      {data?.label && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
              pointerEvents: 'all',
            }}
            className="relative"
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
          >
            <div
              className={`
                px-2 py-1 rounded-full text-sm font-medium border-2 cursor-pointer
                transition-all duration-200 hover:scale-110 hover:shadow-lg
                ${iconStyle.bgColor} ${iconStyle.textColor} ${iconStyle.borderColor} ${iconStyle.shadowColor}
              `}
            >
              {iconStyle.icon}
            </div>
            
            {/* Hover tooltip */}
            {showTooltip && (
              <div
                className={`
                  absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 rounded-lg shadow-lg border z-50
                  ${darkMode 
                    ? 'bg-gray-800 text-gray-200 border-gray-600' 
                    : 'bg-white text-gray-700 border-gray-300'
                  }
                `}
                style={{ whiteSpace: 'nowrap' }}
              >
                {getTooltipContent()}
                {/* Tooltip arrow */}
                <div
                  className={`
                    absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent
                    ${darkMode ? 'border-t-gray-800' : 'border-t-white'}
                  `}
                />
              </div>
            )}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
};
